import { useEffect, useState, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import type { ISiteMapPoint, SiteMapEquipmentType } from '@interfaces/ISiteMap';
import { EQUIPMENT_ACCENT_TOKEN, EQUIPMENT_CATALOG } from './siteMap.constants';
import { distance } from './siteMap.logic';
import type { useSiteMapData } from './useSiteMapData';
import styles from './SiteMap.module.css';

export type SiteMapMode = 'view' | 'draw' | 'placeEquipment' | 'setRadius';

interface IUseEquipmentPlacementArgs {
  mode: SiteMapMode;
  setMode: (mode: SiteMapMode) => void;
  createEquipment: ReturnType<typeof useSiteMapData>['createEquipment'];
  t: TFunction;
}

/**
 * Owns the "place a new equipment marker" flow (spec §4.2, Task 9): catalog
 * pick → first stage click sets the position and enters `setRadius` seeded at
 * the type's `defaultRadius` → stage move tracks the live radius →
 * second click commits via `createEquipment`. `mode` is lifted to `SiteMap`
 * (shared with draw-area mode — only one tool is ever active), everything
 * else about the in-progress placement lives here.
 */
export function useEquipmentPlacement({ mode, setMode, createEquipment, t }: IUseEquipmentPlacementArgs) {
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [equipmentType, setEquipmentType] = useState<SiteMapEquipmentType | null>(null);
  const [equipmentPos, setEquipmentPos] = useState<ISiteMapPoint | null>(null);
  const [draftRadius, setDraftRadius] = useState(0);
  const [cursor, setCursor] = useState<ISiteMapPoint | null>(null);

  const reset = () => {
    setMode('view');
    setEquipmentType(null);
    setEquipmentPos(null);
    setDraftRadius(0);
    setCursor(null);
  };

  // Window-level Escape, mirroring draw mode's own listener — active only
  // while a placement is in flight, so it never shadows Escape elsewhere.
  useEffect(() => {
    if (mode !== 'placeEquipment' && mode !== 'setRadius') return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') reset();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const pick = (type: SiteMapEquipmentType) => {
    setEquipmentType(type);
    setMode('placeEquipment');
  };

  const handleClick = (p: ISiteMapPoint) => {
    if (mode === 'placeEquipment' && equipmentType) {
      const entry = EQUIPMENT_CATALOG.find((c) => c.type === equipmentType);
      setEquipmentPos(p);
      setDraftRadius(entry?.defaultRadius ?? 1);
      setMode('setRadius');
      return;
    }
    // Clicks while the create is in flight are ignored: each would place
    // another copy until onSuccess resets the tool.
    if (mode === 'setRadius' && equipmentType && equipmentPos && !createEquipment.isPending) {
      createEquipment.mutate(
        {
          type: equipmentType,
          // The type key, not the author's translated label: a default name
          // then reads in each viewer's language (`equipmentDisplayName`).
          name: equipmentType,
          position: equipmentPos,
          radius: draftRadius,
        },
        { onSuccess: reset },
      );
    }
  };

  const handleMove = (p: ISiteMapPoint) => {
    setCursor(p);
    if (mode === 'setRadius' && equipmentPos) setDraftRadius(Math.max(1, distance(equipmentPos, p)));
  };

  const draft: ReactNode =
    mode === 'setRadius' && equipmentPos && equipmentType ? (
      <>
        {/* Reuses `.radiusCircle` (the placed-equipment look) rather than a
         *  dedicated draft class — same dashed-accent language, tinted to the
         *  picked type, avoids duplicating the same properties twice. */}
        <circle
          cx={equipmentPos.x}
          cy={equipmentPos.y}
          r={draftRadius}
          className={styles.radiusCircle}
          style={{ stroke: EQUIPMENT_ACCENT_TOKEN[equipmentType], fill: EQUIPMENT_ACCENT_TOKEN[equipmentType] }}
        />
        {cursor ? (
          <line
            x1={equipmentPos.x}
            y1={equipmentPos.y}
            x2={cursor.x}
            y2={cursor.y}
            className={styles.draftRadiusLine}
          />
        ) : null}
      </>
    ) : null;

  const chipDraft =
    mode === 'setRadius' && equipmentType && equipmentPos
      ? { position: equipmentPos, type: equipmentType, label: t(`logistics.siteMap.equipment.${equipmentType}`) }
      : null;

  const hint: ReactNode =
    mode === 'placeEquipment' ? (
      <span className={styles.hintChip}>{t('logistics.siteMap.hint.placeClick')}</span>
    ) : mode === 'setRadius' ? (
      <span className={styles.hintChip}>{t('logistics.siteMap.hint.radiusDrag')}</span>
    ) : null;

  return { catalogOpen, setCatalogOpen, pick, handleClick, handleMove, draft, chipDraft, hint, reset };
}
